    /**
     * THE PRICE COMES FIRST, then the name.
     *
     * A closed native select truncates whatever does not fit, and at 360px — an ordinary Android
     * width — nothing fits at 16px text, in either language. Truncation cuts the END of the line
     * in English and the START of it in Arabic, so with the price last, Arabic showed ٬٤٥٠ ج.م for
     * ٢٬٤٥٠ ج.م: a wrong number about money. With the price first, whatever is lost is the tail of
     * the plan NAME, which the summary line below repeats in full. The price is never wrong.
     */
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    groupPlans, pricePerMonth, savingVsMonthly, formatEgp, formatCount, planName, UNDECIDED_PLAN, type PublicPlan,
} from '@/lib/plans';

interface Props {
    plans: PublicPlan[];
    loading: boolean;
    failed: boolean;
    onRetry: () => void;
    /** A plan id, `UNDECIDED_PLAN`, or '' for nothing chosen yet. */
    value: string;
    onChange: (value: string) => void;
    /** Shown under the field, with aria-invalid and focus moved here on submit. */
    error?: string | null;
    idPrefix?: string;
    label?: string;
}

/**
 * The plan a member asks for at signup.
 *
 * A NATIVE <select> on purpose: on a phone it opens the OS picker — full height, keyboard and
 * screen-reader support, no scroll trap — which a custom dropdown has to reimplement and usually
 * gets wrong on mobile. 16px text, because anything smaller makes iOS Safari zoom the page on
 * focus. The label sits above the field; the placeholder is never the label.
 *
 * Choosing a plan is a REQUEST, not a purchase: nothing here creates a subscription.
 */
export const PlanSelect: React.FC<Props> = ({
    plans, loading, failed, onRetry, value, onChange, error, idPrefix = 'plan', label,
}) => {
    const { t, i18n } = useTranslation();
    const uid = useId();
    const id = `${idPrefix}-${uid}`;
    const errorId = `${id}-error`;
    const chosen = plans.find(p => p.id === value) || null;

    const classesText = (p: PublicPlan) => p.classesPerMonth === null
        ? t('signup.plan.unlimited_classes')
        : t('signup.plan.classes_per_month', { count: p.classesPerMonth, n: formatCount(p.classesPerMonth, i18n.language) });

    /**
     * The name and the price, nothing else. The longer label ("Basic — 8 classes / month — EGP
     * 900") was truncated by the closed select at 390px, and in Arabic the cut falls on the LEFT
     * — it ate the leading digit, so ٢٬٤٥٠ read as ٬٤٥٠. A wrong price on a public page about
     * money. The classes, duration, per-month figure and saving are all in the summary line
     * below, which is never truncated.
     */
    const optionLabel = (p: PublicPlan) =>
        t('signup.plan.option', { price: formatEgp(p.price, i18n.language), name: planName(p, i18n.language) });

    /**
     * The summary as separate isolated parts, not one interpolated sentence. Arabic reorders
     * neutral separators between numbers, so "٣ أشهر · ٢٬٤٥٠ ج.م" rendered as "٣٠ أشهر ٢٬٤٥٠٠" —
     * the digits of two different figures ran together. Each part sits in its own <bdi>.
     */
    const summaryParts = (): string[] => {
        if (!chosen) return [];
        const price = formatEgp(chosen.price, i18n.language);
        if (chosen.months <= 1) return [classesText(chosen), t('signup.plan.per_month', { price })];
        const saving = savingVsMonthly(chosen, plans);
        const perMonth = t('signup.plan.per_month', { price: formatEgp(pricePerMonth(chosen), i18n.language) });
        return [
            classesText(chosen),
            t('signup.plan.months', { count: chosen.months, n: formatCount(chosen.months, i18n.language) }),
            price,
            saving > 0
                ? t('signup.plan.per_month_saving', { perMonth, saving: formatEgp(saving, i18n.language) })
                : perMonth,
        ];
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={id} className="text-base">{label ?? t('signup.plan.label')}</Label>

            {failed ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" role="alert">
                    <p className="text-destructive">{t('signup.plan.load_failed')}</p>
                    <Button type="button" variant="outline" size="sm" className="mt-2 min-h-11" onClick={onRetry}>
                        <RefreshCw className="me-1 h-4 w-4" />{t('signup.plan.retry')}
                    </Button>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <select
                            id={id}
                            value={value}
                            disabled={loading}
                            onChange={e => onChange(e.target.value)}
                            aria-invalid={!!error}
                            aria-describedby={error ? errorId : undefined}
                            // 16px (text-base) keeps iOS Safari from zooming the page on focus.
                            className="w-full min-h-[48px] rounded-md border border-input bg-background px-3 py-2 text-base
                                       focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60
                                       aria-[invalid=true]:border-destructive"
                        >
                            <option value="" disabled>
                                {loading ? t('signup.plan.loading') : t('signup.plan.placeholder')}
                            </option>
                            {groupPlans(plans).map(({ group, plans: inGroup }) => (
                                <optgroup key={group} label={t(`signup.plan.group.${group}`)}>
                                    {inGroup.map(p => <option key={p.id} value={p.id}>{optionLabel(p)}</option>)}
                                </optgroup>
                            ))}
                            {!loading && <option value={UNDECIDED_PLAN}>{t('signup.plan.undecided')}</option>}
                        </select>
                        {loading && (
                            <Loader2 className="pointer-events-none absolute end-9 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                        )}
                    </div>

                    {/* No dir="auto" on the summary: it starts with a digit, which resolves auto
                        to LTR and laid the parts out left-to-right inside the Arabic page. It
                        follows the page direction, and each part is isolated with <bdi>. */}
                    {chosen && (
                        <p className="text-sm text-muted-foreground">
                            {summaryParts().map((part, i) => (
                                <React.Fragment key={part}>
                                    {/* In Arabic the middle dot is the digit zero's glyph, so
                                        "٣ · ٢٬٤٥٠" read as "٣٠ ٢٬٤٥٠٠". Arabic gets a comma. */}
                                    {i > 0 && <span aria-hidden>{i18n.language === 'ar' ? '، ' : ' · '}</span>}
                                    <bdi>{part}</bdi>
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                    {value === UNDECIDED_PLAN && <p className="text-sm text-muted-foreground">{t('signup.plan.undecided_hint')}</p>}
                    <p className="text-xs text-muted-foreground">{t('signup.plan.not_a_payment')}</p>
                </>
            )}

            {error && <p id={errorId} className="text-sm font-medium text-destructive" role="alert">{error}</p>}
        </div>
    );
};
